import { useState, useEffect } from 'react';
import * as SunCalc from 'suncalc';
import { fetchSnowDepth, SnowDepthResult } from '../services/snowService';

export interface DayForecast {
  date: Date;
  weekday: string;       // 'Man', 'Tir', …
  symbol: string;
  maxTemp: number;
  minTemp: number;
  totalRainMm: number;
  maxWindMs: number;
}

export interface WeatherData {
  today: {
    symbol: string;
    maxTemp: number;
    minTemp: number;
    windMs: number;       // m/s nå
    rainMm: number;       // mm neste time
  };
  tomorrow: {
    symbol: string;
    maxTemp: number;
    minTemp: number;
  };
  forecast: DayForecast[]; // 5 dager (inkl. i dag)
}

export interface SunData {
  sunrise: string;
  sunset: string;
  daylightHours: number;
  sunPosition: number; // 0 to 1 representing position in the sky (0 sunrise, 1 sunset)
}

const WEEKDAYS = ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'];

export const useWeatherSun = (lat: number, lon: number) => {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [sun, setSun] = useState<SunData | null>(null);
  const [snow, setSnow] = useState<SnowDepthResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const weatherUrl = `https://api.met.no/weatherapi/locationforecast/2.0/compact?lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}`;
        const [weatherResponse, snowData] = await Promise.all([
          fetch(weatherUrl, {
            headers: { 'User-Agent': 'fricamp-app github.com/jon-takle' }
          }),
          fetchSnowDepth(lat, lon)
        ]);

        if (!weatherResponse.ok) throw new Error('Weather fetch failed');
        const data = await weatherResponse.json();
        setSnow(snowData);

        const timeseries = data.properties.timeseries;
        const now = new Date();

        // ── Today + Tomorrow ──────────────────────────────────────────────────
        const getStatsForDate = (date: Date) => {
          const dayEntries = timeseries.filter((ts: any) => {
            const d = new Date(ts.time);
            return d.getDate() === date.getDate() && d.getMonth() === date.getMonth();
          });
          if (dayEntries.length === 0) return null;

          const temps = dayEntries.map((ts: any) => ts.data.instant.details.air_temperature);
          const winds = dayEntries.map((ts: any) => ts.data.instant.details.wind_speed ?? 0);
          const rains = dayEntries.map((ts: any) =>
            ts.data.next_1_hours?.details?.precipitation_amount ?? 0
          );
          const midday = dayEntries.find((ts: any) => new Date(ts.time).getHours() >= 12) || dayEntries[0];

          return {
            symbol: midday.data.next_12_hours?.summary?.symbol_code
              || midday.data.next_6_hours?.summary?.symbol_code
              || midday.data.next_1_hours?.summary?.symbol_code
              || 'cloudy',
            maxTemp: Math.round(Math.max(...temps)),
            minTemp: Math.round(Math.min(...temps)),
            windMs: Math.round(winds[0] * 10) / 10,            // first entry = current wind
            rainMm: Math.round(rains[0] * 10) / 10,            // first entry = next-hour rain
            maxWindMs: Math.round(Math.max(...winds) * 10) / 10,
            totalRainMm: Math.round(rains.reduce((a: number, b: number) => a + b, 0) * 10) / 10,
          };
        };

        const tomorrow = new Date();
        tomorrow.setDate(now.getDate() + 1);

        const todayStats   = getStatsForDate(now);
        const tomorrowStats = getStatsForDate(tomorrow);

        // ── 5-day forecast ────────────────────────────────────────────────────
        const forecast: DayForecast[] = [];
        for (let i = 0; i < 5; i++) {
          const d = new Date(now);
          d.setDate(now.getDate() + i);
          const stats = getStatsForDate(d);
          if (!stats) continue;
          forecast.push({
            date: d,
            weekday: i === 0 ? 'I dag' : i === 1 ? 'I morgen' : WEEKDAYS[d.getDay()],
            symbol: stats.symbol,
            maxTemp: stats.maxTemp,
            minTemp: stats.minTemp,
            totalRainMm: stats.totalRainMm,
            maxWindMs: stats.maxWindMs,
          });
        }

        if (todayStats && tomorrowStats) {
          setWeather({
            today: {
              symbol:  todayStats.symbol,
              maxTemp: todayStats.maxTemp,
              minTemp: todayStats.minTemp,
              windMs:  todayStats.windMs,
              rainMm:  todayStats.rainMm,
            },
            tomorrow: {
              symbol:  tomorrowStats.symbol,
              maxTemp: tomorrowStats.maxTemp,
              minTemp: tomorrowStats.minTemp,
            },
            forecast,
          });
        }

        // ── Sun Data ──────────────────────────────────────────────────────────
        const times   = SunCalc.getTimes(new Date(), lat, lon);
        const sunrise = times.sunrise;
        const sunset  = times.sunset;
        const daylightMs = sunset.getTime() - sunrise.getTime();
        const daylightHours = Math.round(daylightMs / (1000 * 60 * 60));

        const currentTime = new Date().getTime();
        let sunPos = 0;
        if (currentTime >= sunrise.getTime() && currentTime <= sunset.getTime()) {
          sunPos = (currentTime - sunrise.getTime()) / daylightMs;
        } else if (currentTime > sunset.getTime()) {
          sunPos = 1;
        }

        const locale = window.localStorage.getItem('i18nextLng') || 'no-NO';
        setSun({
          sunrise:      sunrise.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
          sunset:       sunset.toLocaleTimeString(locale,  { hour: '2-digit', minute: '2-digit' }),
          daylightHours,
          sunPosition:  sunPos,
        });

      } catch (err) {
        console.error('WeatherSun Error:', err);
        setError('weather.error');
        setSnow(null);
      } finally {
        setLoading(false);
      }
    };

    if (lat && lon) fetchData();
  }, [lat, lon]);

  return { weather, sun, snow, error, loading };
};
